import moment from 'moment';
import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { BodyMain, PhoneItem } from '@/components';
import { useTimer } from '@/hooks';
import { goalReached, YMGoals } from '@/infrastructure/ym';
import { MainLayout } from '@/layout';
import { UIState } from '@/model/global/types';
import { storeFormPhone, updatePhoneAndRequestConfirm } from '@/model/identification/actions';
import { makeSelectConfirmationState, makeSelectPhone } from '@/model/identification/selectors';
import { IdentificationUIState } from '@/model/identification/types';

const selectPhone = makeSelectPhone();
const selectConfirmationState = makeSelectConfirmationState();

export const LoginPage: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const phone = useSelector(selectPhone);
  const confirmationState = useSelector(selectConfirmationState);
  const [secondsTilRetry] = useTimer(confirmationState?.resendAfter);
  const anotherPhone = phone && !phone.hasError && phone?.modified && secondsTilRetry && secondsTilRetry > 0;
  const anotherPhoneGoalReached = useRef(false);
  if (anotherPhone && !anotherPhoneGoalReached.current) {
    anotherPhoneGoalReached.current = true;
    goalReached(YMGoals.PHONE_CONFIRMATION_CHANGE_TIMEOUT_SHOWN);
  }

  return (
    <MainLayout
      buttons={{
        logout: <FormattedMessage id="identification.logout.button.title" />,
        main: {
          submit: true,
          onAct: async (): Promise<void> => {
            const { error } = await updatePhoneAndRequestConfirm()(dispatch);
            if (!error) {
              history.push(`/${UIState.IDENTIFICATION}/${IdentificationUIState.CONFIRMATION}`);
            }
          },
          disabled:
            // prettier-ignore
            // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
            !phone?.value
            || phone?.value?.length === 0
            || phone?.hasError
            || (phone?.modified && !!secondsTilRetry && secondsTilRetry > 0),
          title: <FormattedMessage id="identification.pages.login.submit.title" />,
        },
      }}
      header={{
        title: <FormattedMessage id="identification.pages.login.title" />,
      }}
      minHeight={300}
    >
      <BodyMain>
        <FormattedMessage id="identification.pages.login.description" />
      </BodyMain>
      <PhoneItem
        value={{
          value: phone?.value,
          hasError: !!phone?.hasError,
        }}
        messages={{
          label: <FormattedMessage id="identification.pages.login.fields.phone.label" />,
        }}
        onChange={async (newPhone: string): Promise<void> => {
          await storeFormPhone({ phone: newPhone })(dispatch);
        }}
      />
      {anotherPhone && (
        <BodyMain style={{ textAlign: 'center' }}>
          <FormattedMessage
            id="identification.pages.login.repeat.description"
            values={{
              timer: moment.utc(moment.duration(secondsTilRetry, 's').asMilliseconds()).format('mm:ss'),
            }}
          />
        </BodyMain>
      )}
    </MainLayout>
  );
};

export default LoginPage;
